import { sampleQueryKeys, studyQueryKeys } from '@/config/queryKeys';
import { apiClient, type DownloadedFile } from '@/lib/api-client';
import type {
  SampleFilters,
  SampleQcSummary,
  SampleSortField,
  SampleWithNamesPublic,
  PaginatedSamplesResponse,
  SampleSearchRequest,
  SortOrder,
} from '@/types';

/** A malformed response repeats identically on every attempt, so it must not burn the backoff. */
export const CONTRACT_BREACH = { status: 502, code: 'CONTRACT_BREACH' } as const;

export async function getSample(orgId: string, sampleId: string): Promise<SampleWithNamesPublic> {
  const result = await apiClient.get<SampleWithNamesPublic>(sampleQueryKeys.endpoints.detail(orgId, sampleId), {
    expectJson: true,
  });

  // With expectJson the only route to null is a 204, which on a detail GET is a backend contract
  // breach, not a missing sample — 502 keeps it terminal without reporting the sample as absent.
  if (result === null) {
    console.error('[getSample] detail endpoint returned an empty body', { orgId, sampleId });
    throw Object.assign(new Error(`Sample ${sampleId}: endpoint returned an empty body`), CONTRACT_BREACH);
  }

  // The response type is asserted, not generated (see the route loader) — without this a shape
  // change ships as a silently half-empty detail page instead of a visible failure.
  if (typeof (result as { id?: unknown }).id !== 'string') {
    console.error('[getSample] unexpected detail response shape', { orgId, sampleId });
    throw Object.assign(new Error(`Sample ${sampleId}: unexpected detail response shape`), CONTRACT_BREACH);
  }

  return result;
}

export async function searchStudySamples(
  orgId: string,
  studyId: string,
  request: SampleSearchRequest,
): Promise<PaginatedSamplesResponse> {
  const result = await apiClient.post<PaginatedSamplesResponse>(
    studyQueryKeys.endpoints.samplesSearch(orgId, studyId),
    request,
    { expectJson: true },
  );
  if (result === null) throw new Error('searchStudySamples: endpoint returned no content');
  return result;
}

// Org-wide QC tallies, deliberately independent of the samples grid's filter state — the
// backend counts every sample in scope, not the current page.
export async function fetchSamplesQcSummary(orgId: string): Promise<SampleQcSummary> {
  const result = await apiClient.get<SampleQcSummary>(sampleQueryKeys.endpoints.qcSummary(orgId), {
    expectJson: true,
  });

  if (result === null) throw new Error('fetchSamplesQcSummary: endpoint returned no content');
  return result;
}

export interface StudySamplesDownloadOptions {
  sortBy?: SampleSortField;
  sortOrder?: SortOrder;
  filters?: SampleFilters | null;
}

/** Streams a study's full sample list as CSV. */
export async function downloadStudySamplesCsv(
  orgId: string,
  studyId: string,
  options: StudySamplesDownloadOptions = {},
): Promise<DownloadedFile> {
  const { filters, sortBy, sortOrder } = options;

  return apiClient.download(studyQueryKeys.endpoints.samplesDownload(orgId, studyId), {
    query: {
      sort_by: sortBy,
      sort_order: sortOrder,
      // The endpoint takes `filters` as a JSON-encoded query param, not a body.
      filters: filters ? JSON.stringify(filters) : undefined,
    },
  });
}
